import React, { FC, useMemo } from 'react';

import {
  Background,
  DefaultDailyBonus,
  FontAwesomeIcon,
  PostPurchase,
  ProductData,
  StoreRefreshPopup
} from '@appcharge/shared-ui';

import useOffersUI from 'api/useOffersUI';
import useTheme from 'api/useTheme';
import { cn } from 'components/lib/utils';
import Typography from 'components/Typography/Typography';

import 'common/styles.scss';

type PreviewSectionProps = {
  publisherId?: string;
  currentOfferUI: string;
  isDailyBonus: boolean;
  isTriggeredPopup?: boolean;
  currentPrice: number;
  products: any;
  productsList: ProductData[];
};

const PreviewSection: FC<PreviewSectionProps> = ({
  publisherId,
  currentOfferUI,
  isDailyBonus,
  isTriggeredPopup = false,
  currentPrice,
  products,
  productsList
}) => {
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const { getOfferUI } = useOffersUI(publisherId, currentOfferUI);

  const previewProducts = useMemo<ProductData[]>(() => {
    if (!products || productsList.length === 0) return [];

    return productsList.flatMap(({ productId, quantity, quantityDisplay }) => {
      const selectedProduct = products!.result.find(
        (product: ProductData) => product.productId === productId
      );
      if (!selectedProduct) return [];

      return [
        {
          ...selectedProduct,
          images: Object.fromEntries(
            selectedProduct.images.map(
              ({ type, url }: { type: string; url: string }) => [type, url]
            )
          ),
          quantity,
          quantityDisplay:
            quantityDisplay ||
            selectedProduct.quantityDisplay ||
            String(quantity)
        }
      ];
    });
  }, [products, productsList]);

  const offerData = getOfferUI?.data;

  let mockId = 'iphone-mock-post-purchase';
  let popupPreview: React.ReactNode;

  if (isTriggeredPopup) {
    mockId = 'iphone-mock-triggered';
    popupPreview = (
      <StoreRefreshPopup
        open
        disableBodyLock
        onClose={() => {}}
        onCollect={() => {}}
        title={offerData?.specialOffer?.title || ''}
        titleColor={offerData?.specialOffer?.fontColor?.colorOne}
        titleWeight={offerData?.specialOffer?.fontWeight}
        backgroundColor={offerData?.specialOffer?.backgroundColor?.colorOne}
        backgroundImage={offerData?.backgroundImage}
        borderColor={offerData?.borderColor}
        borderWidth={offerData?.borderWidth}
        buttonText={`$${currentPrice}`}
        products={previewProducts}
        container={document.getElementById('iphone-mock-triggered')}
      />
    );
  } else if (isDailyBonus) {
    mockId = 'iphone-mock';
    popupPreview = (
      <DefaultDailyBonus
        isPreview
        title={offerData?.specialOffer?.title || ''}
        titleColor={offerData?.specialOffer?.fontColor.colorOne}
        titleWeight={offerData?.specialOffer?.fontWeight}
        titleSize={offerData?.specialOffer?.fontSize.toString()}
        borderWidth={offerData?.borderWidth}
        borderColor={offerData?.borderColor}
        modalContainer={() => document.getElementById('iphone-mock')}
        backgroundImage={offerData?.backgroundImage}
        products={previewProducts}
        onCollect={async () => true}
        onClose={() => {}}
        onAnimationSuccessEndNoPreview={() => {}}
      />
    );
  } else {
    popupPreview = (
      <PostPurchase
        isPreview
        title={offerData?.specialOffer?.title || ''}
        titleColor={offerData?.specialOffer?.fontColor.colorOne}
        titleWeight={offerData?.specialOffer?.fontWeight}
        titleSize={offerData?.specialOffer?.fontSize.toString()}
        borderWidth={offerData?.borderWidth}
        borderColor={offerData?.borderColor}
        modalContainer={() =>
          document.getElementById('iphone-mock-post-purchase')
        }
        backgroundImage={offerData?.backgroundImage}
        products={previewProducts}
        onActionBtnClicked={async () => true}
        onClose={() => {}}
        prices={{
          priceDisplay: `$${currentPrice}`,
          priceInUsdCents: currentPrice
        }}
      />
    );
  }

  return (
    <div className="w-1/2 h-full bg-zinc-100 px-16 py-10 overflow-y-auto">
      <div className="mb-11 flex items-center justify-between">
        <Typography className="text-p-xl font-semibold">Preview</Typography>
        <div className="flex items-center bg-base-white rounded-lg shadow border border-base-sidebarBorder p-1.5">
          <FontAwesomeIcon
            icon="fa-solid fa-mobile"
            className={cn('text-indigo-300 text-[18px] w-8 h-8 cursor-pointer')}
          />
          <hr className="bg-base-border w-[1px] h-6 mx-1" />
          <FontAwesomeIcon
            icon="fa-regular fa-desktop"
            className={cn(
              'text-neutral-300 text-[18px] w-8 h-8 cursor-not-allowed'
            )}
          />
        </div>
      </div>
      <div className="flex justify-center">
        <div
          className="iphone-mock"
          id={mockId}
        >
          <Background
            backgroundImageMobile={
              !generalTheme?.isLoading &&
              generalTheme.data?.backgroundImageMobile
            }
            backgroundImageDesktop={
              !generalTheme.isLoading &&
              generalTheme.data?.backgroundImageDesktop
            }
            width="100%"
            height="100%"
            position="absolute"
          />
          {popupPreview}
        </div>
      </div>
    </div>
  );
};

export default PreviewSection;
